import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Lock, Mail, Eye, EyeOff, Loader2, User, Phone, Building, KeyRound, ArrowLeft,
  Plus, DollarSign, Users, CheckCircle, Clock, Send, FileText, TrendingUp, Home
} from 'lucide-react';
import ResetPasswordForm from '../components/ResetPasswordForm';
import { getToken, setSession, clearSession } from '../lib/authSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferrerLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referrer, setReferrer] = useState(null);
  
  // Dashboard tab state
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotResult, setForgotResult] = useState(null);
  
  // Change password states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState('');
  const [changeSuccess, setChangeSuccess] = useState(false);
  
  // Referrals state
  const [referrals, setReferrals] = useState([]);
  const [showNewReferralModal, setShowNewReferralModal] = useState(false);
  const [referralForm, setReferralForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    service_type: 'mortgage',
    notes: ''
  });
  const [referralLoading, setReferralLoading] = useState(false);

  // Fetch referrals
  const fetchReferrals = async () => {
    if (!referrer) return;
    try {
      const response = await fetch(`${API_URL}/api/referrers/${referrer.id}/referrals`);
      if (response.ok) {
        const data = await response.json();
        setReferrals(data);
      }
    } catch (err) {
      console.error('Failed to fetch referrals:', err);
    }
  };

  useEffect(() => {
    if (referrer) {
      fetchReferrals();
    }
  }, [referrer]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/referrers/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      setSession('referrer', data.token);
      setReferrer(data.referrer);
      localStorage.setItem('referrer', JSON.stringify(data.referrer));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setReferrer(null);
    clearSession('referrer');
    localStorage.removeItem('referrer');
    setEmail('');
    setPassword('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotResult(null);

    try {
      const response = await fetch(`${API_URL}/api/referrers/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });

      const data = await response.json();
      setForgotResult(data);
    } catch (err) {
      setForgotResult({ message: 'Failed to process request. Please try again.' });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangeError('');
    setChangeSuccess(false);

    if (newPassword !== confirmPassword) {
      setChangeError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setChangeError('Password must be at least 8 characters');
      return;
    }

    setChangeLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/referrers/${referrer.id}/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to change password');
      }

      setChangeSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setShowChangePassword(false);
        setChangeSuccess(false);
      }, 2000);
    } catch (err) {
      setChangeError(err.message);
    } finally {
      setChangeLoading(false);
    }
  };

  const handleSubmitReferral = async (e) => {
    e.preventDefault();
    setReferralLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/referrers/${referrer.id}/referrals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(referralForm)
      });

      if (!response.ok) {
        throw new Error('Failed to submit referral');
      }

      setShowNewReferralModal(false);
      setReferralForm({
        client_name: '',
        client_email: '',
        client_phone: '',
        service_type: 'mortgage',
        notes: ''
      });
      fetchReferrals();
    } catch (err) {
      console.error('Failed to submit referral:', err);
    } finally {
      setReferralLoading(false);
    }
  };

  // Check for stored referrer on mount
  useEffect(() => {
    const storedReferrer = localStorage.getItem('referrer');
    if (storedReferrer && getToken('referrer')) {
      setReferrer(JSON.parse(storedReferrer));
    } else if (storedReferrer) {
      localStorage.removeItem('referrer');
    }
    const onExpired = (e) => {
      if (e.detail?.role === 'referrer') {
        clearSession('referrer');
        localStorage.removeItem('referrer');
        setReferrer(null);
        setError('Your session has expired. Please sign in again.');
      }
    };
    window.addEventListener('utmost-auth-expired', onExpired);
    return () => window.removeEventListener('utmost-auth-expired', onExpired);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'paid': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Forgot Password Modal
  const resetToken = new URLSearchParams(window.location.search).get('reset');
  if (resetToken && !referrer) {
    return (
      <ResetPasswordForm
        token={resetToken}
        endpoint="/api/referrers/reset-password"
        onDone={() => { window.history.replaceState({}, '', '/referrer-login'); window.location.reload(); }}
      />
    );
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003D5C] via-[#005A8C] to-[#0077B6] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <button
              onClick={() => { setShowForgotPassword(false); setForgotResult(null); setForgotEmail(''); }}
              className="flex items-center text-gray-500 hover:text-gray-700 text-sm mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Login
            </button>
            <CardTitle className="text-2xl font-bold text-[#003D5C]">Reset Password</CardTitle>
            <p className="text-gray-500 text-sm">Enter your email and we'll send you a secure reset link</p>
          </CardHeader>
          <CardContent>
            {forgotResult ? (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-green-50 border border-green-200" data-testid="forgot-result">
                  <p className="text-sm">{forgotResult.message}</p>
                  <p className="text-xs text-gray-500 mt-2">The link expires in 1 hour. Check your spam folder if you don't see it.</p>
                </div>
                <Button
                  onClick={() => { setShowForgotPassword(false); setForgotResult(null); setForgotEmail(''); }}
                  className="w-full bg-[#0077B6] hover:bg-[#005A8C]"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-[#0077B6] hover:bg-[#005A8C]"
                >
                  {forgotLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Reset Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Login Form
  if (!referrer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003D5C] via-[#005A8C] to-[#0077B6] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8960C] rounded-full flex items-center justify-center shadow-lg">
                <Users className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center text-[#003D5C]">Referrer Portal</CardTitle>
            <p className="text-center text-gray-500 text-sm">Sign in to track your referrals and commissions</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                    placeholder="your@email.com"
                    required
                    data-testid="referrer-email-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                    placeholder="Enter your password"
                    required
                    data-testid="referrer-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0077B6] hover:bg-[#005A8C] text-white font-semibold py-2"
                data-testid="referrer-login-button"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Sign In'}
              </Button>
              
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="w-full text-center text-sm text-[#0077B6] hover:underline"
              >
                Forgot your password?
              </button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-500">
                Want to become a referrer?{' '}
                <a href="/contact" className="text-[#0077B6] hover:underline">Contact us</a>
              </p>
              <a href="/" className="inline-flex items-center gap-1 mt-3 text-sm text-gray-500 hover:text-[#0077B6]">
                <ArrowLeft className="h-4 w-4" />
                Back to Main Website
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#003D5C] text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a 
              href="/" 
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              title="Go to Main Website"
            >
              <Home className="h-5 w-5" />
              <span className="hidden sm:inline text-sm">Main Website</span>
            </a>
            <div className="h-8 w-px bg-white/30 hidden sm:block"></div>
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#B8960C] rounded-full flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Referrer Portal</h1>
              <p className="text-blue-200 text-sm">Welcome, {referrer.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowChangePassword(true)}
              className="border-white/30 text-white hover:bg-white/10"
            >
              <KeyRound className="h-4 w-4 mr-2" />
              Change Password
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-white/30 text-white hover:bg-white/10"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Referrals</p>
                  <p className="text-3xl font-bold">{referrer.total_referrals || 0}</p>
                </div>
                <Users className="h-10 w-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Total Earned</p>
                  <p className="text-3xl font-bold">${(referrer.total_earned || 0).toLocaleString()}</p>
                </div>
                <DollarSign className="h-10 w-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Commission Rate</p>
                  <p className="text-3xl font-bold">{referrer.commission_rate || 0}%</p>
                </div>
                <TrendingUp className="h-10 w-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Pending</p>
                  <p className="text-3xl font-bold">
                    {referrals.filter(r => r.status === 'pending' || r.status === 'contacted').length}
                  </p>
                </div>
                <Clock className="h-10 w-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'dashboard' 
                ? 'border-[#0077B6] text-[#0077B6]' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('referrals')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'referrals' 
                ? 'border-[#0077B6] text-[#0077B6]' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Referrals
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'profile' 
                ? 'border-[#0077B6] text-[#0077B6]' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Profile
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Quick Actions</h2>
              <Button
                onClick={() => setShowNewReferralModal(true)}
                className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Submit New Referral
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No referrals yet</p>
                    <p className="text-sm">Submit your first referral to get started!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {referrals.slice(0, 5).map(referral => (
                      <div key={referral.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-800">{referral.client_name}</p>
                          <p className="text-sm text-gray-500">{referral.service_type} - {new Date(referral.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(referral.status)}`}>
                          {referral.status.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">All Referrals</h2>
              <Button
                onClick={() => setShowNewReferralModal(true)}
                className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Referral
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                {referrals.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Send className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No referrals yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-4 font-medium text-gray-600">Client</th>
                          <th className="text-left p-4 font-medium text-gray-600">Contact</th>
                          <th className="text-left p-4 font-medium text-gray-600">Service</th>
                          <th className="text-left p-4 font-medium text-gray-600">Date</th>
                          <th className="text-left p-4 font-medium text-gray-600">Status</th>
                          <th className="text-left p-4 font-medium text-gray-600">Commission</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {referrals.map(referral => (
                          <tr key={referral.id} className="hover:bg-gray-50">
                            <td className="p-4">
                              <p className="font-medium text-gray-800">{referral.client_name}</p>
                            </td>
                            <td className="p-4">
                              <p className="text-sm text-gray-600">{referral.client_email}</p>
                              <p className="text-sm text-gray-500">{referral.client_phone}</p>
                            </td>
                            <td className="p-4">
                              <span className="capitalize text-gray-700">{referral.service_type}</span>
                            </td>
                            <td className="p-4 text-gray-600">
                              {new Date(referral.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(referral.status)}`}>
                                {referral.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="p-4 font-medium text-gray-800">
                              {referral.commission_amount > 0 ? `$${referral.commission_amount.toLocaleString()}` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl">
            <Card>
              <CardHeader>
                <CardTitle>My Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8960C] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {referrer.name?.charAt(0) || 'R'}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{referrer.name}</h3>
                    <p className="text-gray-500 capitalize">{referrer.referrer_type} Referrer</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <Mail className="h-4 w-4" />
                      <span className="text-sm">Email</span>
                    </div>
                    <p className="font-medium">{referrer.email}</p>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <Phone className="h-4 w-4" />
                      <span className="text-sm">Phone</span>
                    </div>
                    <p className="font-medium">{referrer.phone}</p>
                  </div>
                  
                  {referrer.company && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <Building className="h-4 w-4" />
                        <span className="text-sm">Company</span>
                      </div>
                      <p className="font-medium">{referrer.company}</p>
                    </div>
                  )}
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <TrendingUp className="h-4 w-4" />
                      <span className="text-sm">Commission Rate</span>
                    </div>
                    <p className="font-medium">{referrer.commission_rate || 0}%</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Member since {new Date(referrer.created_at).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* New Referral Modal */}
      {showNewReferralModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Submit New Referral</CardTitle>
              <button onClick={() => setShowNewReferralModal(false)} className="text-gray-400 hover:text-gray-600">
                <span className="text-2xl">&times;</span>
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitReferral} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                  <input
                    type="text"
                    value={referralForm.client_name}
                    onChange={(e) => setReferralForm({ ...referralForm, client_name: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                    required
                    data-testid="referral-client-name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Email *</label>
                  <input
                    type="email"
                    value={referralForm.client_email}
                    onChange={(e) => setReferralForm({ ...referralForm, client_email: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                    required
                    data-testid="referral-client-email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Phone *</label>
                  <input
                    type="tel"
                    value={referralForm.client_phone}
                    onChange={(e) => setReferralForm({ ...referralForm, client_phone: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                    required
                    data-testid="referral-client-phone"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Type *</label>
                  <select
                    value={referralForm.service_type}
                    onChange={(e) => setReferralForm({ ...referralForm, service_type: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                    data-testid="referral-service-type"
                  >
                    <option value="mortgage">Mortgage</option>
                    <option value="insurance">Insurance</option>
                    <option value="investment">Investment</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                  <textarea
                    value={referralForm.notes}
                    onChange={(e) => setReferralForm({ ...referralForm, notes: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                    rows="3"
                    data-testid="referral-notes"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewReferralModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={referralLoading}
                    className="flex-1 bg-[#0077B6] hover:bg-[#005A8C]"
                    data-testid="submit-referral-button"
                  >
                    {referralLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Submit Referral'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Change Password</CardTitle>
              <button onClick={() => { setShowChangePassword(false); setChangeError(''); setChangeSuccess(false); }} className="text-gray-400 hover:text-gray-600">
                <span className="text-2xl">&times;</span>
              </button>
            </CardHeader>
            <CardContent>
              {changeSuccess ? (
                <div className="text-center py-4">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-green-600 font-medium">Password changed successfully!</p>
                </div>
              ) : (
                <form onSubmit={handleChangePassword} className="space-y-4">
                  {changeError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                      {changeError}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                      required
                      minLength={8}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                      required
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setShowChangePassword(false); setChangeError(''); }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={changeLoading}
                      className="flex-1 bg-[#0077B6] hover:bg-[#005A8C]"
                    >
                      {changeLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Change Password'}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReferrerLoginPage;
